"use client";

import { useEffect } from "react";

import {
  SUPPLY_OUTLET_COOKIE,
  SUPPLY_OUTLET_COOKIE_MAX_AGE,
  encodeOutletCookie,
} from "../_lib/outlet-cookie";

/**
 * Persists the real outlet code into `fb_supply_outlet`. This is the write
 * half of the fix for bare /supply links; the read half is server-side, in
 * ../_lib/outlet-server.ts, which resolves the outlet for any request that
 * arrives without `?outlet=` (validated against the session's own scope).
 *
 * It exists for the same reason SupplyNavCookieSync and SupplyTierCookieSync
 * do: a route-level loading.tsx has no session, so it cannot resolve the
 * outlet itself, and neither can the several HQ desk pages (spend, interco,
 * hygiene, match, credits) that are legitimately outlet-agnostic themselves
 * but still render rail links to outlet-scoped surfaces.
 *
 * Rendered by SupplyTabBar only when a page passed a real (truthy)
 * outletCode; loading skeletons and the HQ desk pages above pass null and
 * this does not mount, leaving the cookie — and therefore the next paint's
 * links — at whatever outlet was last known. That is deliberate: null must
 * never clobber a real, previously-known outlet.
 *
 * Writing a cookie from a Server Component would force the layout dynamic
 * and break PPR, and isn't allowed during render — hence a mount effect.
 *
 * Unlike SupplyNavCookieSync/SupplyTierCookieSync, this does NOT also write
 * a `data-supply-*` attribute on the page root. Those two exist so CSS can
 * hide/show already-rendered elements ([data-supply-nav~="…"] /
 * [data-supply-tier="floor"]) before the cookie has round-tripped through a
 * navigation. Outlet has no CSS consumer and needs none: the current page
 * already resolved its own outlet server-side, so there is nothing on THIS
 * page for an attribute to correct.
 */
export function SupplyOutletCookieSync({ outletCode }: { outletCode: string }) {
  const value = encodeOutletCookie(outletCode);
  useEffect(() => {
    document.cookie = `${SUPPLY_OUTLET_COOKIE}=${value}; path=/; max-age=${SUPPLY_OUTLET_COOKIE_MAX_AGE}; SameSite=Lax`;
  }, [value]);

  return <span data-supply-outlet-sync hidden />;
}
